import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { AUDIT_BY_RECORD, AUDIT_ALL, AUDIT_BY_METER } from 'graphql/audit.gql';
import { message } from 'antd';

type Params = { page?: number; limit?: number };

export const useAuditData = (
  table?: string,
  pkValue?: string,
  startDate?: string,
  endDate?: string,
  meterId?: number | null | undefined,
  skipQuery?: boolean,
) => {
  const [pageVars, setPageVars] = useState({ limit: 10, offset: 0 });

  const query = meterId ? AUDIT_BY_METER : pkValue ? AUDIT_BY_RECORD : AUDIT_ALL;

  const variables = meterId
    ? { meterId, startDate, endDate, ...pageVars }
    : pkValue
    ? { table, pkValue, ...pageVars }
    : { startDate, endDate, ...pageVars };

  // Run query, recompute variables every render
  const { data, loading, error, refetch } = useQuery(query, {
    variables,
    fetchPolicy: 'cache-and-network',
    skip: !!skipQuery,
  });

  // Show error if any
  useEffect(() => {
    if (error) message.error(error.message || 'Failed to fetch audit events');
  }, [error]);

  // Handle param updates (pagination, limit changes)
  const setParams = useCallback((params: Params) => {
    setPageVars((v) => ({
      limit: params.limit ?? v.limit,
      offset: params.page ? (params.page - 1) * (params.limit ?? v.limit) : v.offset,
    }));
  }, []);

  // Normalize data
  const events = data?.auditByMeter ?? data?.auditByRecord ?? data?.auditAll ?? [];

  // If backend doesn't return total count, estimate it
  const totalCount =
    events.length === pageVars.limit
      ? pageVars.offset + events.length + 1 // assume more exist
      : pageVars.offset + events.length;

  return {
    data: events,
    totalCount,
    loading,
    error,
    setError: () => {},
    setParams,
    refetch,
    currentPage: Math.floor(pageVars.offset / pageVars.limit) + 1,
    pageSize: pageVars.limit,
    setCurrentPage: (page: number) => {
      setPageVars((v) => ({
        ...v,
        offset: (page - 1) * v.limit,
      }));
    },
    setPageSize: (size: number) => {
      setPageVars((v) => ({
        limit: size,
        offset: v.offset,
      }));
    },
    onChangePage: (page: number, size: number) => {
      setPageVars({
        limit: size,
        offset: (page - 1) * size,
      });
    },
  };
};
